import React from 'react';
import { 
  ListItem, 
  Typography, 
  Paper, 
  Box, 
  Grid, 
  Chip,
  Card,
  CardContent,
  CardMedia
} from '@mui/material';
import { 
  People,
  PersonAdd,
  TrendingUp,
  Whatshot,
  ShoppingCart,
  LocalOffer
} from '@mui/icons-material';

// Helper function to truncate text to 32 characters
const truncateText = (text, maxLength = 32) => {
  if (!text) return '-';
  const str = String(text);
  return str.length > maxLength ? str.substring(0, maxLength) + '...' : str;
};

// Component to render AI labels as badges
const AILabelsSection = ({ aiLabels }) => {
  if (!aiLabels || aiLabels.length === 0) return null;

  const getLabelColor = (confidence) => {
    switch (confidence) {
      case 'high':
        return 'secondary'; // Purple
      case 'med':
        return 'error'; // Red  
      case 'low':
        return 'warning'; //Orange
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        AI Labels
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {aiLabels.map((label) => {
            return <Chip
              key={label.id}
              label={label.id}
              color={getLabelColor(label.confidence)}
              size="small"
              variant="outlined"
            />
        })}
      </Box>
    </Box>
  );
};

// Component to render basic coin info
const CoinInfoSection = ({ coin, hit }) => {
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 1
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {coin.zora_name} ({coin.zora_symbol})
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {coin.active && (
            <Chip 
              label="Active"
              size="small"
              variant="filled"
              color="success"
              sx={{ borderRadius: 0.5 }}
            />
          )}
          {coin.zora_media_content_type && (
            <Chip 
              label={truncateText(coin.zora_media_content_type, 32)}
              size="small"
              variant="filled"
              color="info"
              sx={{ borderRadius: 0.5 }}
            />
          )}
          <Chip 
            label={coin.zora_chain_id || '-'}
            size="small"
            variant="filled"
            color="secondary"
            sx={{ borderRadius: 0.5 }}
          />
          <Chip 
            label={hit._score != null ? hit._score.toFixed(2) : '-'}
            size="small"
            variant="outlined"
            color="primary"
          />
        </Box>
      </Box>
      {coin.zora_description && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          "{coin.zora_description}"
        </Typography>
      )}
    </Box>
  );
};

// Component to render market metrics
const MarketMetricsSection = ({ coin }) => {
  const metrics = [
    { label: 'Price', value: `$${coin.zora_price_in_usdc?.toFixed(8)}`, icon: '💰' },
    { label: 'Market Cap', value: `$${coin.zora_market_cap?.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, icon: '📊' },
    { label: 'Market Cap Δ 24h', value: `$${coin.zora_market_cap_delta_24h?.toFixed(2)}`, color: coin.zora_market_cap_delta_24h >= 0 ? 'success' : 'error', icon: '📈' },
    { label: 'Volume 24h', value: `$${coin.zora_volume_24h?.toFixed(2)}`, icon: '💵' },
    { label: 'Total Volume', value: `$${coin.zora_total_volume?.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, icon: '📈' },
    { label: 'Total Supply', value: coin.zora_total_supply?.toLocaleString(), icon: '🔢' },
    { label: 'Unique Holders', value: coin.zora_unique_holders?.toLocaleString(), icon: '👥' }
  ];

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Market Metrics
      </Typography>
      <Grid container spacing={1}>
        {metrics.map((metric, idx) => (
          <Grid key={idx}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {metric.icon} {metric.label}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 600,
                  color: metric.color === 'success' ? '#2e7d32' : metric.color === 'error' ? '#d32f2f' : 'text.primary',
                  wordBreak: 'break-word'
                }}
              >
                {metric.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// Component to render timestamps
const TimestampsSection = ({ coin }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Timeline
      </Typography>
      <Grid container spacing={1}>
        {coin.zora_created_at && (
          <Grid>
            <Paper
              elevation={0}
              sx={{
                p: 1,
                backgroundColor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Created
              </Typography>
              <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                {formatDate(coin.zora_created_at)}
              </Typography>
            </Paper>
          </Grid>
        )}
        {coin.zora_updated_at && (
          <Grid>
            <Paper
              elevation={0}
              sx={{
                p: 1,
                backgroundColor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Updated
              </Typography>
              <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                {formatDate(coin.zora_updated_at)}
              </Typography>
            </Paper>
          </Grid>
        )}
        {coin.last_interaction_timestamp && (
          <Grid>
            <Paper
              elevation={0}
              sx={{
                p: 1,
                backgroundColor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Last Trade
              </Typography>
              <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                {formatDate(coin.last_interaction_timestamp)}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

// Component to render engagement metrics with icons
const EngagementSection = ({ coin }) => {
  const engagementMetrics = [
    { key: 'num_buy', value: coin.num_buy, icon: ShoppingCart, label: 'Buys' },
    { key: 'num_sell', value: coin.num_sell, icon: LocalOffer, label: 'Sells' },
    { key: 'score_popular', value: coin.score_popular, icon: Whatshot, label: 'Popularity' },
    { key: 'score_trending', value: coin.score_trending, icon: TrendingUp, label: 'Trending' }
  ];

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Engagement
      </Typography>
      <Grid container spacing={1}>
        {engagementMetrics.map((o) => (
          <Grid key={o.key}>
            <Box sx={{ 
              p: 1, 
              backgroundColor: 'grey.50', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 'fit-content'
            }}>
              <o.icon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {o.value != null ? o.value.toFixed(2) : '-'}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// Component to render spam and moderation metrics
const SpamModerationSection = ({ coin }) => {
  const spamModerationMetrics = [
    { key: 'score_spam', value: coin.score_spam, label: 'Spam' },
    { key: 'score_not_ok', value: coin.score_not_ok, label: 'Not-Ok' },
    { key: 'user_score_spam', value: coin.user_score_spam, label: 'Farcaster User Spam' },
    { key: 'user_events_num', value: coin.user_events_num, label: 'FarcasterUser Num Events' },
    { key: 'user_events_per_day', value: coin.user_events_per_day, label: 'FarcasterUser Events/Day' },
    { key: 'user_warpcast_spam', value: coin.user_warpcast_spam, label: 'FarcasterWarpcast Label' }
  ];

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Spam & Moderation
      </Typography>
      <Grid container spacing={1}>
        {spamModerationMetrics.map((o) => (
          <Grid key={o.key}>
            <Box sx={{ 
              p: 1, 
              backgroundColor: 'grey.50', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 'fit-content'
            }}>
              <Typography variant="caption" color="text.secondary">
                {o.label}: {o.value != null ? o.value.toFixed(2) : '-'}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// Component to render Farcaster author features
const FarcasterAuthorSection = ({ coin }) => {
  const user_pfp = coin.user_pfp;
  const user_name = coin.user_name;
  const user_id = coin.zora_creator_farcaster_id;
  const num_follower = coin.num_follower;
  const num_following = coin.num_following;

  if (!user_id) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Farcaster Author
      </Typography>
      <Box sx={{ 
        p: 1, 
        backgroundColor: 'grey.50', 
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'grey.200',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        {user_pfp && (
          <Box
            component="img"
            src={user_pfp}
            alt={user_name || 'User avatar'}
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              objectFit: 'cover',
              flexShrink: 0
            }}
          />
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: 32, gap: 0.25 }}>
          {user_id && (
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1, fontSize: '0.65rem' }}>
              #{user_id}
            </Typography>
          )}
          {user_name && (
            <Typography variant="caption" color="text.primary" sx={{ lineHeight: 1 }}>
              @{user_name}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          {num_follower != null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <People sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {num_follower.toFixed(0)}
              </Typography>
            </Box>
          )}
          {num_following != null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <PersonAdd sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {num_following.toFixed(0)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Component to render document info
const DocumentInfoSection = ({ hit, coin }) => {
  return (
    <Box sx={{ mb: 2, pt: 2, borderTop: '1px solid', borderColor: 'grey.200' }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Coin ID
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all', flex: 1 }}>
          {hit._id}
        </Typography>
      </Box>
    </Box>
  );
};

const ZoraCoinCard = ({ hit }) => {
  const { _source: coin } = hit;
  
  return (
    <ListItem 
      sx={{ 
        borderBottom: '1px solid #e0e0e0',
        flexDirection: 'column',
        alignItems: 'stretch',
        py: 2,
        px: 0
      }}
    >
      <Card
        elevation={1}
        sx={{
          width: '100%',
          backgroundColor: '#fafafa',
          overflow: 'visible'
        }}
      >
        {/* Image Section */}
        {coin.zora_preview_medium_url && (
          <CardMedia
            component="img"
            height="200"
            image={coin.zora_preview_medium_url}
            alt={coin.zora_name}
            sx={{ 
              objectFit: 'cover',
              backgroundColor: '#e0e0e0'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        )}

        <CardContent sx={{ pb: 2 }}>
          {/* Coin Info */}
          <CoinInfoSection coin={coin} hit={hit} />

          {/* AI Labels */}
          {(() => {
            const aiLabels_high = coin.ai_labels_high || [];
            const aiLabels_med = coin.ai_labels_med || [];
            const aiLabels_low = coin.ai_labels_low || [];
            const aiLabelsMap = {}
            for (const label of aiLabels_high) {
              aiLabelsMap[label] = 'high';
            }
            for (const label of aiLabels_med) {
              if (!(label in aiLabelsMap)) {
                aiLabelsMap[label] = 'med';
              }
            }
            for (const label of aiLabels_low) {
              if (!(label in aiLabelsMap)) {
                aiLabelsMap[label] = 'low';
              }
            }
            const aiLabels = Object.keys(aiLabelsMap).map((label) => ({
              id: label,
              confidence: aiLabelsMap[label]
            }));
            return <AILabelsSection aiLabels={aiLabels} />;
          })()}

          {/* Market Metrics */}
          <MarketMetricsSection coin={coin} />

          {/* Engagement */}
          <EngagementSection coin={coin} />

          {/* Spam & Moderation */}
          <SpamModerationSection coin={coin} />

          {/* Timestamps */}
          <TimestampsSection coin={coin} />

          {/* Farcaster Author */}
          <FarcasterAuthorSection coin={coin} />

          {/* Document Info */}
          <DocumentInfoSection hit={hit} coin={coin} />
        </CardContent>
      </Card>
    </ListItem>
  );
};

export default ZoraCoinCard;

